import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TimeWindowPicker, presetToDateRange } from '../components/TimeWindowPicker';
import { ServiceFilter } from '../components/ServiceFilter';
import { AccountPicker } from '../components/AccountPicker';
import { KPICard, KPIGrid } from '../components/KPICards';
import { SpendChart } from '../components/SpendChart';
import { CampaignTable } from '../components/CampaignTable';
import { AdTable } from '../components/AdTable';
import { useMetaAdsData } from '../hooks/useMetaAdsData';
import { apiFetch } from '../lib/api';
import { formatBRL, formatPercent, formatNumber } from '../lib/format';
import type { ServiceLabel, TimeWindow, MetaAccount } from '../types';

export default function MetaAds() {
  const [searchParams, setSearchParams] = useSearchParams();
  const preset = (searchParams.get('preset') ?? '30d') as TimeWindow['preset'];
  const service = (searchParams.get('service') ?? 'all') as ServiceLabel;
  const account = searchParams.get('account') ?? 'all';

  const { startDate, endDate } = presetToDateRange(preset);

  const [metaAccounts, setMetaAccounts] = useState<MetaAccount[]>([]);

  useEffect(() => {
    apiFetch<{ meta: { accounts: MetaAccount[] } }>('/api/metrics/financeiro')
      .then(d => setMetaAccounts(d.meta.accounts))
      .catch(() => {});
  }, []);

  function setPreset(p: TimeWindow['preset']) { setSearchParams(sp => { sp.set('preset', p); return sp; }); }
  function setService(s: ServiceLabel) { setSearchParams(sp => { sp.set('service', s); return sp; }); }
  function setAccount(a: string) { setSearchParams(sp => { sp.set('account', a); return sp; }); }

  const { kpis, timeseries, campaigns, ads, isLoading } = useMetaAdsData({
    startDate, endDate, service, account,
  });

  const metaKPIs = kpis?.meta;

  return (
    <div style={{ padding: '28px', maxWidth: '1440px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <div style={{
              width: '8px', height: '8px', borderRadius: '50%',
              background: 'var(--green)', flexShrink: 0,
            }} className="pulse-dot" />
            <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}>
              Meta Ads
            </span>
          </div>
          <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: 'var(--text-primary)' }}>Meta Ads</h1>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text-tertiary)' }}>Relatório de desempenho</p>
        </div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <AccountPicker accounts={metaAccounts} value={account} onChange={setAccount} />
          <ServiceFilter value={service} onChange={setService} />
          <TimeWindowPicker value={preset} onChange={setPreset} />
        </div>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
          <div className="spinner" />
        </div>
      ) : (
        <>
          {/* KPIs */}
          <KPIGrid columns={4}>
            <KPICard label="Valor Usado" value={formatBRL(metaKPIs?.valorUsado ?? 0)} accentColor="var(--accent)" delay={0} />
            <KPICard label="Alcance" value={formatNumber(metaKPIs?.alcance ?? 0)} accentColor="var(--blue)" delay={0.04} />
            <KPICard label="CTR" value={formatPercent(metaKPIs?.ctr ?? 0)} accentColor="var(--green)" delay={0.08} />
            <KPICard label="CPM" value={formatBRL(metaKPIs?.cpm ?? 0)} delay={0.12} />
            <KPICard label="Frequência" value={(metaKPIs?.frequencia ?? 0).toFixed(2)} delay={0.16} />
            <KPICard label="Resultados" value={formatNumber(metaKPIs?.resultados ?? 0)} accentColor="var(--green)" delay={0.20} />
            <KPICard label="Custo/Resultado" value={formatBRL(metaKPIs?.custoPorResultado ?? 0)} accentColor="var(--red)" delay={0.24} />
          </KPIGrid>

          {/* Chart */}
          <div style={{ marginTop: '24px' }}>
            <SpendChart data={timeseries} showMeta height={200} />
          </div>

          {/* Campaign Table */}
          <div style={{ marginTop: '24px' }}>
            <h2 style={{ margin: '0 0 12px', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Campanhas
            </h2>
            <CampaignTable campaigns={campaigns} />
          </div>

          {/* Ad Table */}
          <div style={{ marginTop: '24px' }}>
            <h2 style={{ margin: '0 0 12px', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Anúncios
            </h2>
            <AdTable ads={ads} />
          </div>
        </>
      )}
    </div>
  );
}
